import React, { useState, useEffect } from 'react';
import {
  Box, Tabs, Tab, Grid, Paper, Dialog, DialogTitle, IconButton,
  Typography, Alert, useTheme, useMediaQuery, Drawer, Button, AppBar, Toolbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MenuIcon from '@mui/icons-material/Menu';
import PatientList from './components/PatientList';
import PatientDetail from './components/PatientDetail';
import PatientForm from './components/PatientForm';
import {
  getPatients, createPatient, updatePatient, deletePatient,
} from '../../api/patient.api';

function PatientsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [tab, setTab] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const response = await getPatients();
      setPatients(response.patients || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching patients:', err);
      setError('Failed to load patients. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPatient = () => setAddDialogOpen(true);
  const handleCloseAddDialog = () => setAddDialogOpen(false);

  const handleSavePatient = async (patientData) => {
    try {
      if (patientData._id) {
        await updatePatient(patientData._id, patientData);
      } else {
        await createPatient(patientData);
      }
      setAddDialogOpen(false);
      setTab(0);
      fetchPatients();
    } catch (err) {
      console.error('Error saving patient:', err);
      setError('Failed to save patient. Please try again.');
    }
  };

  const handlePatientDelete = async (patient) => {
    if (!window.confirm(`Delete patient ${patient.fullName || `${patient.firstName} ${patient.lastName}`}?`)) {
      return;
    }
    try {
      await deletePatient(patient._id);
      fetchPatients();
      if (selectedPatient?._id === patient._id) {
        setSelectedPatient(null);
      }
    } catch (err) {
      console.error('Error deleting patient:', err);
      setError('Failed to delete patient. Please try again.');
    }
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  // Mobile layout
  if (isMobile) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <AppBar position="static" elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1 }}>
              Patients
            </Typography>
            <Button
              color="inherit"
              startIcon={<PersonAddIcon />}
              onClick={handleAddPatient}
            >
              Add
            </Button>
          </Toolbar>
        </AppBar>

        {error && (
          <Alert severity="error" sx={{ m: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ flex: 1, p: 1 }}>
          <Paper elevation={2} sx={{ height: '100%', p: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
              <Tab label="Details" />
              <Tab label="Edit" />
            </Tabs>
            <Box sx={{ mt: 2, overflow: 'auto', height: 'calc(100% - 60px)' }}>
              {tab === 0 && (
                <PatientDetail
                  patient={selectedPatient}
                  onEdit={(patient) => {
                    setSelectedPatient(patient);
                    setTab(1);
                  }}
                />
              )}
              {tab === 1 && (
                <PatientForm
                  patient={selectedPatient}
                  onSave={handleSavePatient}
                  onCancel={() => setTab(0)}
                  mode="edit"
                />
              )}
            </Box>
          </Paper>
        </Box>

        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: isSmallMobile ? '100%' : 320 } }}
        >
          <PatientList
            patients={patients}
            loading={loading}
            onPatientSelect={handlePatientSelect}
            onPatientEdit={(patient) => {
              setSelectedPatient(patient);
              setTab(1);
              setMobileDrawerOpen(false);
            }}
            onPatientDelete={handlePatientDelete}
            onAddPatient={handleAddPatient}
            selectedPatient={selectedPatient}
          />
        </Drawer>

        <Dialog
          open={addDialogOpen}
          onClose={handleCloseAddDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
          >
            <PersonAddIcon />
            <Typography variant="h6" sx={{ flex: 1 }}>Add New Patient</Typography>
            <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <PatientForm
              onSave={handleSavePatient}
              onCancel={handleCloseAddDialog}
              mode="add"
            />
          </Box>
        </Dialog>
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 3 }, height: '100vh', display: 'flex', flexDirection: 'column',
    }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
        {/* Patient List */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <PatientList
              patients={patients}
              loading={loading}
              onPatientSelect={handlePatientSelect}
              onPatientEdit={(patient) => {
                setSelectedPatient(patient);
                setTab(1);
              }}
              onPatientDelete={handlePatientDelete}
              onAddPatient={handleAddPatient}
              selectedPatient={selectedPatient}
            />
          </Paper>
        </Grid>

        {/* Patient Details/Edit */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={2}
            sx={{
              height: '100%', p: 2, display: 'flex', flexDirection: 'column',
            }}
          >
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="Details" />
              <Tab label="Edit" />
            </Tabs>
            <Box sx={{ mt: 2, flex: 1, overflow: 'auto' }}>
              {tab === 0 && (
                <PatientDetail
                  patient={selectedPatient}
                  onEdit={(patient) => {
                    setSelectedPatient(patient);
                    setTab(1);
                  }}
                />
              )}
              {tab === 1 && (
                <PatientForm
                  patient={selectedPatient}
                  onSave={handleSavePatient}
                  onCancel={() => setTab(0)}
                  mode="edit"
                />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Add Patient Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <PersonAddIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Add New Patient
          </Typography>
          <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <PatientForm onSave={handleSavePatient} onCancel={handleCloseAddDialog} mode="add" />
        </Box>
      </Dialog>
    </Box>
  );
}

export default PatientsPage;
